import React from 'react'
import PropTypes from 'prop-types'
import entityTypes from './enums/entityTypes'
import Tags from './components/Tags'
import icons from './enums/icons'
import PostDetailsDisplay from './components/Item/PostDetailsDisplay'
import ExpandableKitItems from './components/ExpandableKitItems'
import FeedPostComment from './components/FeedPostComment'
import Kudos from './components/Kudos'
import ButtonWithIcon from './components/ButtonWithIcon'
import urlForPostComment from './utilities/urlForPostComment'
import urlForPostShare from './utilities/urlForPostShare'
import urlForPostEdit from './utilities/urlForPostEdit'
import PostTitle from './components/Item/PostTitle'
import ImageCarousel from './components/ImageCarousel'
import connectPropTypes from './utilities/connectPropTypes'
import getNormalisedParameters from './utilities/getNormalisedParameters'
import Thing from './utilities/Data/Thing'
import PostSchema from './utilities/Data/Post'
import styles from './Post.icss'
import getDecodedURIComponent from './utilities/getDecodedURIComponent'
import Item from './utilities/Data/Item'

//	Grab the right colour itemImages
const getItemImages = (myItem) => {
	const item = { ...myItem }
	const images = Item.getImages(item.item, item)
	return images
}

class Post extends React.Component {
	static getDerivedStateFromProps(props) {
		const result = props.post ?
			{ hideFromFeed: !!props.post.hideFromFeed } :
			null
		return result
	}

	constructor(props) {
		super(props)
		const parsedSearch = getNormalisedParameters(this.props.location.search)
		this.state = {
			postId: getDecodedURIComponent(parsedSearch.postid || props.match.params.postId)
		}

		this.detailsRef = React.createRef()

		this.handleHideFromFeed = this.handleHideFromFeed.bind(this)
		this.handleClickHeader = this.handleClickHeader.bind(this)
	}

	componentDidMount() {
		this.props.appHeading({
			text: this.context.translate('Post'),
			icon: icons.whiteActivity
		})
		if (this.state.postId) {
			this.props.postFetch({ postId: this.state.postId, uid: this.props.user.uid })
		}
	}

	componentDidUpdate(prevProps, prevState) {
		if (this.props.post && (!prevProps.post || (prevState.hideFromFeed !== this.state.hideFromFeed))) {
			const post = this.props.post
			const showAdminContextMenu = this.props.user.isAdmin
			if (showAdminContextMenu) {
				this.props.appAddContextMenu({
					menuKey: 'hidePostFromFeedContextMenu',
					func: this.handleHideFromFeed,
					text: this.state.hideFromFeed ? this.context.translate('Show in feed') : this.context.translate('Hide from feed')
				})
			}

			const isOwnProfile = post.uid === this.props.user.uid

			if (isOwnProfile) {
				this.props.appAddContextMenu({
					menuKey: 'editPostContextMenu',
					func: () => this.props.appRedirect(urlForPostEdit({ postId: post.postId, kitId: post.kit.kitId })),
					text: this.context.translate('Edit post')
				})
			}
		}
	}

	handleHideFromFeed() {
		this.setState((state) => {
			const hideFromFeed = !state.hideFromFeed
			this.props.postHideFromFeedSet({ postId: state.postId, hideFromFeed })
			return { hideFromFeed }
		})
	}

	handleClickHeader() {
		clearTimeout(this.scrollTimer)
		this.scrollTimer = setTimeout(() => {
			this.detailsRef.current.scrollIntoView({ block: 'start', behavior: 'smooth' })
		}, 0)
	}

	render() {
		const { post, itemOwnByKey, itemWantByKey } = this.props

		if (!post) {
			return null
		}

		const postHasImages = (post.images && post.images.length !== 0) || post.imageUrl
		const imageContent = PostSchema.postImages(post)
		const kudos = (this.props.postKudosById && this.props.postKudosById[post.postId] !== undefined ? !!this.props.postKudosById[post.postId] : undefined)
		const kitItems = post.kit && post.kit.items
		const subtitle = PostSchema.feedCollectionProps.itemSubtitle(post)
		const title = PostSchema.feedCollectionProps.itemTitle(post)
		const counts = PostSchema.feedCollectionProps.itemCounts(post)

		return (
			<section>
				<div className={styles.post}>
					<PostTitle
						iconName="blackActivity"
						onClick={this.handleClickHeader}
						subtitle={subtitle}
						counts={counts}
						title={title}
					/>

					<section className={styles.leadImage}>
						<Tags
							readOnly
							hideTags={!postHasImages}
							hideTagsInitially
							tags={post.tags}
							clickTag={(tag) => {
								this.props.appRedirect(Thing.href(tag))
							}}
						>
							{imageContent && (
								<ImageCarousel images={imageContent} />
							)}
						</Tags>
					</section>

					<section className={styles.statsContainer}>
						<section className={styles.buttonContainer}>
							<Kudos
								kudos={kudos}
								kudosSet={kudosValue => this.props.kudosPostSet({ kudos: kudosValue, post })}
								kudosCount={post.kudosCount}
							/>
						</section>
						<section className={styles.buttonContainer}>
							<ButtonWithIcon iconName="blackComments" onClick={() => this.props.appRedirect(urlForPostComment({ postId: post.postId }))}>
								<span>{this.context.translate('{countComments|# comment|# comments}', { countComments: post.countComments || 0 })}</span>
							</ButtonWithIcon>
						</section>
						<section className={styles.buttonContainer}>
							<ButtonWithIcon iconName="blackShare" onClick={() => this.props.appRedirect(urlForPostShare({ postId: post.postId }))}>
								<span>{this.context.translate('Share')}</span>
							</ButtonWithIcon>
						</section>
					</section>

					{post.caption && post.caption !== '' && (
						<FeedPostComment myPost={post} showAddButton={false} />
					)}

					{kitItems && (
						<ExpandableKitItems
							classes={{ itemRoot: styles.itemRoot }}
							items={kitItems}
							showAllIfTooFewItems
							displayItems={kitItems.length}
							itemTitle={kitItem => Thing.title(kitItem.item)}
							itemSubtitle={kitItem => Thing.subtitle(kitItem.item)}
							itemImageContent={kitItem => getItemImages(kitItem)}
							itemHref={kitItem => Thing.href(kitItem.item)}
							itemOnSelect={kitItem => this.props.appRedirect(Thing.href({ ...kitItem.item, colour: { colourName: kitItem.colourName } }))}
							itemShowOwnWant
							itemOwn={kitItem => Thing.isOwned({ itemOwnByKey })(kitItem.item, kitItem.colourName)}
							itemWant={kitItem => Thing.isWanted({ itemWantByKey })(kitItem.item, kitItem.colourName)}
							itemOwnSet={Thing.propertyGetter({
								[entityTypes.ITEM.type]: kitItem => own => this.props.itemOwnSet({ own, item: kitItem })
							})}
							itemWantSet={Thing.propertyGetter({
								[entityTypes.ITEM.type]: kitItem => want => this.props.itemWantSet({ want, item: kitItem })
							})}
						/>
					) }

					<section ref={this.detailsRef}>
						<PostDetailsDisplay post={post} />
					</section>
				</div>
			</section>
		)
	}
}

Post.contextTypes = {
	translate: PropTypes.func
}

Post.propTypes = {
	location: PropTypes.shape({
		pathname: PropTypes.string.isRequired,
		search: PropTypes.string
	}).isRequired,
	match: PropTypes.shape({
		params: PropTypes.object.isRequired
	}).isRequired,
	history: PropTypes.shape({
		goBack: PropTypes.func.isRequired
	}).isRequired,
	appAddContextMenu: PropTypes.func.isRequired,
	appRedirect: PropTypes.func.isRequired,
	appHeading: PropTypes.func.isRequired,
	post: PropTypes.object, // eslint-disable-line react/forbid-prop-types
	user: PropTypes.object.isRequired, // eslint-disable-line react/forbid-prop-types
	postFetch: PropTypes.func.isRequired,
	postHideFromFeedSet: PropTypes.func.isRequired,
	itemOwnSet: PropTypes.func.isRequired,
	itemWantSet: PropTypes.func.isRequired,
	kudosPostSet: PropTypes.func.isRequired,
	postKudosById: PropTypes.object, // eslint-disable-line react/forbid-prop-types
	itemOwnByKey: PropTypes.object, // eslint-disable-line react/forbid-prop-types
	itemWantByKey: PropTypes.object // eslint-disable-line react/forbid-prop-types
}

Post.defaultProps = {
	post: undefined,
	postKudosById: undefined,
	itemOwnByKey: undefined,
	itemWantByKey: undefined
}

export const testing = {
	Post
}

export default connectPropTypes(Post)



// WEBPACK FOOTER //
// ./src/Post.jsx